import React, { useState } from "react";
import cx from "classnames";
import Icon from "../Icon/Icon.jsx";

import { object, string, arrayOf, shape, bool } from "prop-types";

const propTypes = {
  dictionary: object,
  backgroundColor: string,
  legal: string,
  questions: arrayOf(
    shape({
      question: string,
      eyebrow: string,
      subtext: string,
      description: string,
      options: arrayOf(
        shape({
          title: string, // answer ttext
          answerText: string,
          isCorrect: bool,
        }),
      ),
    }),
  ),
};

/**
 * A component that allows you to answer questions
 * from the data given.
 *
 * @param {object} props
 * @property {object} model
 * @property {object} dictionary
 * data from sitecore
 *
 * @variable {string} statusOfQuestion useState variable that will show the answerText after an answer button is pressed
 * @variable {object} currentQuestion useState variable of the current question in the original model.question array
 * @variable {number} indexInQuestions useState variable the current index being used to keep track where you are in the array
 * @variable {bool}   isAnswered useState variable if the answer button has been clicked or not
 * @variable {number} trackClick useState variable of the click index of the answer button
 * @variable {bool}   isDisabled useState variable for the answer buttons if they should be disabled or not
 * @variable {object} cn classNames
 *
 * @func handleAnswer
 * onClick event for answer buttons that set the state for multiple variables
 *
 * @func handleNextQuestion onClick event for next question button. Sets up the state for the next question in the orginal array
 * @func iconTemplate A template for the icons in the answer buttons
 *
 * @returns <Quiz />
 *
 *  quiz--dark on the mount
 *  if there is a color passed in the model then add container--background to the below what would be the js-mount
 *  darkmode just changes the color we need to pass the bg color in
 */

const Quiz = (props) => {
  const { backgroundColor, legal, questions = [], dictionary } = props;

  const [statusOfQuestion, setStatusOfQuestion] = useState();
  const [currentQuestion, setCurrentQuestion] = useState(questions[0]);
  const [indexInQuestions, setIndexInQuestions] = useState(1);
  const [isAnswered, setIsAnswered] = useState(false);
  const [trackClick, setTrackClick] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);

  // Quiz doesn't do anything without questions.
  if (!questions || questions.length < 1) {
    return null;
  }

  const cn = cx("quiz", {
    "quiz--background": backgroundColor,
  });

  const backgroundStyle = backgroundColor
    ? { "--background-color": backgroundColor }
    : {};

  const handleAnswer = (idx) => {
    setIsAnswered(true);
    setTrackClick(idx);
    setIsDisabled(true);
    setStatusOfQuestion(currentQuestion.options[idx].answerText);
  };

  const handleNextQuestion = () => {
    setIndexInQuestions(indexInQuestions + 1);
    setCurrentQuestion(questions[indexInQuestions]);
    setStatusOfQuestion("");
    setIsDisabled(false);
    setIsAnswered(false);
  };

  const iconTemplate = (idx) => {
    let icon;
    if (currentQuestion.options[idx].isCorrect && isAnswered) {
      icon = <Icon iconName="checkmark" />;
    } else if (
      isAnswered &&
      !currentQuestion.options[idx].isCorrect &&
      trackClick === idx
    ) {
      icon = <Icon iconName="close" />;
    } else {
      icon = null;
    }
    return icon;
  };

  return (
    <section className={cn} style={backgroundStyle}>
      <div className="quiz__container">
        {currentQuestion && (
          <div className="question question__container">
            <header>
              <div className="question__eyebrow">
                <span>{currentQuestion.eyebrow}</span>
                <span>{`${indexInQuestions}/${questions.length}`}</span>
              </div>
              <h3 className="question__question">
                <span>{currentQuestion.question}</span>
              </h3>
              <p className="question__subtext">{currentQuestion.subtext}</p>
            </header>
            <div className="question__answer-list">
              <ol className="answers" role="list">
                {currentQuestion.options.map((answer, idx) => (
                  <li
                    key={idx}
                    className={cx("answer", {
                      "answer--correct": answer.isCorrect && isAnswered,
                      "answer--incorrect":
                        !answer.isCorrect && isAnswered && trackClick === idx,
                    })}
                  >
                    <button
                      type="button"
                      className="answer__button btn btn-lg"
                      onClick={() => handleAnswer(idx)}
                      disabled={isDisabled}
                    >
                      <span>{answer.title}</span>
                      {iconTemplate(idx)}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
        <footer className="quiz__footer">
          {currentQuestion.description && !statusOfQuestion && (
            <div
              className="quiz__description"
              dangerouslySetInnerHTML={{ __html: currentQuestion.description }}
            ></div>
          )}
          {statusOfQuestion && (
            <div className="quiz__status">
              <p className="quiz__status__copy" aria-live="polite">
                {statusOfQuestion}
                <span className="u-visuallyhidden">
                  {dictionary.ariaInCorrect}
                </span>
              </p>
              {indexInQuestions !== questions.length && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => handleNextQuestion()}
                >
                  <span>{dictionary.nextQuestion}</span>
                  <Icon iconName="arrow-right" />
                </button>
              )}
            </div>
          )}
          <div className="quiz__legal">
            <div className="" dangerouslySetInnerHTML={{ __html: legal }}></div>
          </div>
        </footer>
      </div>
    </section>
  );
};
Quiz.propTypes = propTypes;
export default Quiz;
